import { Controller } from "@hotwired/stimulus"

// The title review screen: approve the agent's suggested titles from the
// keyboard or in batches, without reloading. Server rules live in
// Admin::VideosController (accept_title, set_title, batch_accept_title).
//   Arrow keys move between rows; Enter approves the row (or saves an edit);
//   E edits the title in place; Space selects; Esc leaves the edit box;
//   P (or the thumbnail, or "play") opens the video in an overlay, Esc closes it;
//   N hides a video that is not CHDS content, R restores one.
export default class extends Controller {
  static targets = ["row", "input", "check", "waiting", "reviewed", "status", "error", "selected", "toggleLink", "overlay", "frame", "closeButton"]
  static values = { batchUrl: String }

  connect() {
    this.onKey = this.onKey.bind(this)
    this.element.addEventListener("keydown", this.onKey)
    if (this.rowTargets[0]) this.rowTargets[0].focus()
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.onKey)
  }

  csrf() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  async send(method, url, body) {
    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrf() },
      credentials: "same-origin",
      body: body ? JSON.stringify(body) : undefined
    })
    let data = {}
    try { data = await response.json() } catch (_) { /* a non-JSON error page */ }
    if (!response.ok) throw new Error(data.error || `The server answered ${response.status}`)
    return data
  }

  rowOf(el) { return el.closest("[data-title-review-target='row']") }
  inputOf(row) { return row.querySelector("[data-title-review-target='input']") }

  onKey(event) {
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (this.overlayOpen()) {
      if (event.key === "Escape") { event.preventDefault(); this.closeOverlay() }
      return
    }
    const row = this.rowOf(event.target)
    if (!row) return
    const typing = event.target.matches("input[type='text']")
    if (typing) {
      if (event.key === "Enter") { event.preventDefault(); this.save(row) }
      if (event.key === "Escape") { event.preventDefault(); this.inputOf(row).value = this.inputOf(row).dataset.original; row.focus() }
      return
    }
    if (event.target.matches("button, input, a")) return
    switch (event.key) {
      case "ArrowDown": event.preventDefault(); this.move(row, 1); break
      case "ArrowUp": event.preventDefault(); this.move(row, -1); break
      case "Enter": event.preventDefault(); this.save(row); break
      case "e": case "E": event.preventDefault(); { const i = this.inputOf(row); i.focus(); i.select() } break
      case " ": event.preventDefault(); { const c = row.querySelector("[data-title-review-target='check']"); c.checked = !c.checked; this.selectionChanged() } break
      case "p": case "P": event.preventDefault(); this.openOverlay(row); break
      case "n": case "N": if (!this.hiddenView()) { event.preventDefault(); this.setRelevance(row, "not_chds") } break
      case "r": case "R": if (this.hiddenView()) { event.preventDefault(); this.setRelevance(row, "chds") } break
    }
  }

  move(row, step) {
    const rows = this.rowTargets
    const next = rows[rows.indexOf(row) + step]
    if (next) { next.focus(); next.scrollIntoView({ block: "nearest" }) }
  }

  approveRow(event) { this.save(this.rowOf(event.target)) }

  hiddenView() { return new URLSearchParams(window.location.search).get("hidden") === "1" }

  // --- the video overlay -----------------------------------------------------
  overlayOpen() { return this.hasOverlayTarget && !this.overlayTarget.hidden }

  play(event) {
    event.preventDefault()
    this.openOverlay(this.rowOf(event.target))
  }

  openOverlay(row) {
    this.lastRow = row
    this.frameTarget.src = row.dataset.playUrl
    this.frameTarget.title = row.dataset.playTitle || "Video preview"
    this.overlayTarget.hidden = false
    this.closeButtonTarget.focus()
  }

  closeOverlay() {
    this.frameTarget.src = "about:blank"
    this.overlayTarget.hidden = true
    if (this.lastRow && this.lastRow.isConnected) this.lastRow.focus()
  }

  overlayClick(event) {
    if (event.target === this.overlayTarget) this.closeOverlay()
  }

  // --- selection ---------------------------------------------------------------
  selectionChanged() {
    let n = 0
    this.rowTargets.forEach((row) => {
      const on = row.querySelector("[data-title-review-target='check']").checked
      row.classList.toggle("is-selected", on)
      if (on) n += 1
    })
    if (this.hasSelectedTarget) this.selectedTarget.textContent = `${n} selected`
  }

  // --- hide / restore (not CHDS content) ------------------------------------------
  notChds(event) { return this.setRelevance(this.rowOf(event.target), "not_chds") }
  restore(event) { return this.setRelevance(this.rowOf(event.target), "chds") }

  async setRelevance(row, relevance) {
    try {
      row.classList.add("opacity-50")
      const data = await this.send("PATCH", row.dataset.setRelevanceUrl, { relevance })
      this.done(row, data)
    } catch (error) {
      row.classList.remove("opacity-50")
      this.showError(row, error.message)
    }
  }

  // Enter on a row: approve the suggestion as it is, or, if the box was changed
  // (or the agent had none), save what is typed as the person's own title.
  async save(row) {
    const input = this.inputOf(row)
    const typed = input.value.trim()
    const hasSuggestion = row.dataset.hasSuggestion === "true"
    if (!typed) { this.showError(row, "Type a title first."); input.focus(); return }
    try {
      row.classList.add("opacity-50")
      let data
      if (hasSuggestion && typed === input.dataset.original) {
        data = await this.send("PATCH", row.dataset.acceptUrl)
      } else {
        data = await this.send("PATCH", row.dataset.setUrl, { video: { title: typed } })
      }
      this.done(row, data)
    } catch (error) {
      row.classList.remove("opacity-50")
      this.showError(row, error.message)
    }
  }

  showError(row, message) {
    const el = row.querySelector("[data-title-review-target='error']")
    el.textContent = message
    el.hidden = false
  }

  done(row, counts) {
    const next = this.rowTargets[this.rowTargets.indexOf(row) + 1] || this.rowTargets[this.rowTargets.indexOf(row) - 1]
    row.remove()
    this.counts(counts)
    this.selectionChanged()
    if (next) next.focus()
  }

  counts(data) {
    if (data.waiting !== undefined || data.hidden !== undefined) {
      const hiddenView = this.hiddenView()
      const shown = hiddenView ? data.hidden : data.waiting
      if (shown !== undefined) this.waitingTarget.textContent = `${shown} ${hiddenView ? "hidden" : "waiting"}`
      if (this.hasToggleLinkTarget) {
        if (hiddenView && data.waiting !== undefined) this.toggleLinkTarget.textContent = `Back to the review list (${data.waiting} waiting)`
        if (!hiddenView && data.hidden !== undefined) this.toggleLinkTarget.textContent = `${data.hidden} likely not CHDS-relevant: show`
      }
    }
    if (data.reviewed !== undefined) this.reviewedTarget.textContent = `${data.reviewed} reviewed`
  }

  approveSelected() {
    const ids = this.rowTargets.filter((r) => r.querySelector("[data-title-review-target='check']").checked).map((r) => r.dataset.id)
    return this.batch(ids, "Select at least one video first.")
  }

  // Deliberate: only suggestions the agent was sure of AND did not doubt.
  approveHighConfidence() {
    const rows = this.rowTargets.filter((r) => r.dataset.confidence === "high" && r.dataset.doubted === "false" && r.dataset.hasSuggestion === "true")
    if (rows.length === 0) { this.statusTarget.textContent = "No high-confidence suggestions on this page."; return }
    if (!window.confirm(`Approve ${rows.length} high-confidence titles as the agent wrote them? They become public.`)) return
    return this.batch(rows.map((r) => r.dataset.id))
  }

  async batch(ids, emptyMessage) {
    if (ids.length === 0) { this.statusTarget.textContent = emptyMessage; return }
    this.statusTarget.textContent = `Approving ${ids.length}…`
    try {
      const data = await this.send("POST", this.batchUrlValue, { ids })
      this.rowTargets.filter((r) => ids.includes(r.dataset.id)).forEach((r) => r.remove())
      this.counts(data)
      this.statusTarget.textContent = `Approved ${data.approved}${data.skipped ? `, skipped ${data.skipped}` : ""}.`
      if (this.rowTargets[0]) this.rowTargets[0].focus()
    } catch (error) {
      this.statusTarget.textContent = error.message
    }
  }
}
